/** Address grammar for the Companies page: /contacts/<slug>[/<section>].
 *  Person addresses (/contacts/<name>--<key>) share the prefix; a company slug never contains "--"
 *  because slugifySegment collapses runs of separators, so the two cannot be confused. */

// The slug helpers live in companySlugs so the pure directory code can use them.
export { INDIVIDUALS_SLUG, employerCompanySlug, clientCompanySlug } from './companySlugs';

export interface CompanyRoute {
  slug: string;
  section?: string;
}

export function buildCompanyPath(slug: string, section?: string): string {
  const base = `/contacts/${slug}`;
  return section && section !== 'details' && section !== 'overview' ? `${base}/${section}` : base;
}

export function parseCompanyPath(pathname: string): CompanyRoute | null {
  // Documents also names the open folder, as further segments the file browser reads itself.
  const match = pathname.match(/^\/contacts\/([^/]+)(?:\/(documents)\/.+|\/([^/]+))?\/?$/);
  if (!match) return null;
  const slug = decodeURIComponent(match[1]);
  if (slug.includes('--')) return null;
  const section = match[2] ?? match[3];
  return { slug, section: section ? decodeURIComponent(section) : undefined };
}

/** The record address the user just left for the Companies list. The list mounts (and the controller's
 *  address sync runs) while the browser still shows that record's URL, and both would read it as an
 *  instruction to reopen the record. Cleared by any history navigation, which is a real address change. */
let leftRecordAddress: string | null = null;
let clearOnPopstateBound = false;

export function markReturnToCompanies(): void {
  const path = window.location.pathname;
  if (!parseCompanyPath(path)) return;
  leftRecordAddress = path;
  if (clearOnPopstateBound) return;
  clearOnPopstateBound = true;
  window.addEventListener('popstate', () => { leftRecordAddress = null; });
}

export const isLeftCompanyAddress = (): boolean =>
  leftRecordAddress !== null && window.location.pathname === leftRecordAddress;

/** handleSelectClient pushes the client's history entry itself, before the view change. The address sync
 *  that follows would otherwise push a second entry for the same address, so one Back lands on the
 *  entry just left and the client reopens. True when the current entry already is that push. */
export function isPushedClientEntry(
  entry: { view?: string; clientId?: string | null } | null | undefined,
  currentView: string,
  selectedClientId: string | null,
  urlIsStale: boolean,
): boolean {
  return !urlIsStale && !!selectedClientId && entry?.view === currentView
    && (currentView === 'workspaces' || currentView === 'client-detail') && entry.clientId === selectedClientId;
}

/** Companies page -> controller: `{ id, section? }` opens the employer profile; no detail opens the add form. */
export const COMPANIES_OPEN_EMPLOYER_EVENT = 'companies:open-employer';
export const COMPANIES_NEW_EMPLOYER_EVENT = 'companies:new-employer';
